"use client"

import { useState, useEffect } from "react"
import { getUserProfile, api } from "@/lib/api"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { SidebarTrigger } from "@/components/ui/sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { Search, Zap, MapPin, Send, Building2, Bookmark, Activity, Settings } from "lucide-react"
import { Link } from "react-router"

interface DashboardStats {
  total_non_operational_projects: number
  total_operational_projects: number
  total_non_operational_capacity: number
  total_operational_capacity: number
  total_non_operational_developers: number
  total_operational_developers: number
  total_bookmarks: number
  total_developer_bookmarks: number
  total_project_bookmarks: number
}

interface UserPreference {
  id: string
  project_types: string[]
  project_capacity: { min: number; max: number }
  project_region: string[]
}

interface ProjectDeveloper {
  id: string
  role: string
  developer: {
    id: string
    name: string
  }
}

interface Project {
  id: string
  name: string
  technology: string | null
  lifecycleStage: string | null
  region: string | null
  country: string | null
  developers: ProjectDeveloper[]
}

export function DashboardContent() {
  const [query, setQuery] = useState("")
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [preferredProjects, setPreferredProjects] = useState<Project[]>([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [userPreference, setUserPreference] = useState<UserPreference | null>(null)

  const userProfile = getUserProfile()
  const firstName = userProfile?.first_name ?? "there"

  useEffect(() => {
    api
      .get<{ data: DashboardStats }>("/dashboard/stats")
      .then((res) => setStats(res.data))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!userProfile?.id) {
      setLoadingProjects(false)
      return
    }

    api
      .get<{ data: UserPreference }>(`/users/preferences/${userProfile.id}`)
      .then((res) => {
        const prefs = res.data
        setUserPreference(prefs)

        const params = new URLSearchParams({ limit: "5" })

        if (prefs.project_types?.[0]) params.set("technology", prefs.project_types[0])
        if (prefs.project_region?.[0]) params.set("region", prefs.project_region[0])
        if (prefs.project_capacity?.min != null) params.set("min_capacity", String(prefs.project_capacity.min))
        if (prefs.project_capacity?.max != null) params.set("max_capacity", String(prefs.project_capacity.max))

        return api.get<{ data: { data: Project[] } }>(`/projects?${params.toString()}`)
      })
      .then((res) => {
        if (res) setPreferredProjects(res.data.data ?? [])
      })
      .catch(() => {})
      .finally(() => setLoadingProjects(false))
  }, [userProfile?.id])

  const statCards = [
    {
      title: "Non-Operational Projects",
      value: stats ? stats.total_non_operational_projects.toLocaleString() : "—",
      description: "Projects not yet operational",
      icon: <Activity className="h-4 w-4 text-primary" />,
    },
    {
      title: "Non-Operational Capacity",
      value: stats ? `${stats.total_non_operational_capacity.toLocaleString()} MW` : "—",
      description: "Total non-operational capacity",
      icon: <Zap className="h-4 w-4 text-primary" />,
    },
    {
      title: "Total Developers",
      value: stats
        ? (stats.total_non_operational_developers + stats.total_operational_developers).toLocaleString()
        : "—",
      description: "Across all development stages",
      icon: <Building2 className="h-4 w-4 text-primary" />,
    },
    {
      title: "Project Bookmarks",
      value: stats ? stats.total_project_bookmarks.toLocaleString() : "—",
      description: "Projects you're tracking",
      icon: <Bookmark className="h-4 w-4 text-primary" />,
    },
  ]

  const preferenceLabel = userPreference
    ? [
        userPreference.project_types?.[0],
        userPreference.project_region?.[0],
        userPreference.project_capacity
          ? `${userPreference.project_capacity.min}-${userPreference.project_capacity.max} MW`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border px-4 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>Overview</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </header>

      <div className="flex flex-1 flex-col gap-6 p-6">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{`Welcome back, ${firstName}`}</h1>
          <p className="text-muted-foreground mt-1">{"Here's an overview of the renewable energy connection queue"}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => (
            <Card key={stat.title} className="border-border">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.title}</CardTitle>
                {stat.icon}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* AI Search Section */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              {"AI-Powered Search"}
            </CardTitle>
            <CardDescription>
              {"Ask questions about projects, regions, developers, or connection queue status in natural language"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Which solar projects in UKPN region are awaiting connection?"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1"
              />
              <Button>
                <Send className="h-4 w-4 mr-2" />
                {"Ask"}
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                {"Battery storage in Scotland"}
              </Badge>
              <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                {"Offshore wind in SE England"}
              </Badge>
              <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                {"Projects by SSE Renewables"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Preferred Projects */}
        <Card className="border-border">
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle>{"Preferred Projects"}</CardTitle>
                <CardDescription className="mt-1">
                  {preferenceLabel
                    ? `Filtered by your preferences: ${preferenceLabel}`
                    : "Projects matching your preferences"}
                </CardDescription>
              </div>
              {userPreference && (
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {userPreference.project_types?.map((t) => (
                    <Badge key={t} variant="outline" className="text-xs">
                      {t}
                    </Badge>
                  ))}
                  {userPreference.project_region?.map((r) => (
                    <Badge key={r} variant="outline" className="text-xs">
                      <MapPin className="h-2.5 w-2.5 mr-1" />
                      {r}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingProjects ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
                ))}
              </div>
            ) : preferredProjects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground">
                <Settings className="h-8 w-8 mb-3 opacity-40" />
                <p className="text-sm font-medium">{"No projects match your preferences"}</p>
                <p className="text-xs mt-1">{"Try updating your project type, region, or capacity range"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {preferredProjects.map((project) => {
                  const developerNames = project.developers
                    ?.map((d) => d.developer.name)
                    .join(", ")

                  return (
                    <Link
                      key={project.id}
                      to={`/dashboard/projects/${project.id}`}
                      className="flex items-start gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/1rink-0">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-semibold text-foreground truncate">{project.name}</h4>
                          {project.lifecycleStage && (
                            <Badge variant="secondary" className="shrink-0 text-xs capitalize">
                              {project.lifecycleStage}
                            </Badge>
                          )}
                        </div>
                        {developerNames && (
                          <p className="text-sm text-muted-foreground mb-2">{developerNames}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {project.technology && (
                            <span className="flex items-center gap-1">
                              <Zap className="h-3 w-3" />
                              {project.technology}
                            </span>
                          )}
                          {project.region && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {project.region}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            )}
            <Button variant="outline" className="w-full mt-4 bg-transparent" asChild>
              <Link to="/dashboard/projects">{"View All Projects"}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
